(() => {
  const CACHE_KEY = 'rungne:local-rep-eligibility:v1';
  const DISMISS_KEY = 'rungne:local-rep-dismissed:v1';
  const CLICK_KEY = 'rungne:local-rep-clicked:v1';
  const PAGEVIEW_KEY = 'rungne:local-rep-pageviews:v1';
  const SHOWN_KEY = 'rungne:local-rep-shown:v1';

  const storage = {
    get(area, key) {
      try { return window[area].getItem(key); } catch (_) { return null; }
    },
    set(area, key, value) {
      try { window[area].setItem(key, value); } catch (_) { /* private mode */ }
    }
  };

  const number = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const visible = (node) => {
    if (!node || node.hidden) return false;
    const rect = node.getBoundingClientRect();
    const style = window.getComputedStyle(node);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  };

  const anotherPromptIsOpen = (self) => {
    if (document.documentElement.classList.contains('has-modal-open')) return true;
    const selectors = [
      '[role="dialog"][aria-modal="true"]',
      '.klaviyo-form',
      '[data-testid="klaviyo-form"]',
      '#pandectes-banner',
      '#CookieBanner[open]',
      '#CartDrawer[open]',
      '#SearchDrawer[open]',
      '.sticky-buy-button',
      'rungne-event-popup:not([hidden])'
    ];
    return selectors.some((selector) => Array.from(document.querySelectorAll(selector)).some((node) => node !== self && !self.contains(node) && visible(node)));
  };

  const track = (eventName) => {
    const payload = { path: window.location.pathname, device: window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop' };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, rungne: payload });
    if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(eventName, payload);
  };

  class Db2LocalRepPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.closeButton = this.querySelector('[data-rep-close]');
      this.applyLink = this.querySelector('[data-rep-apply]');
      this.closeButton?.addEventListener('click', () => this.dismiss());
      this.applyLink?.addEventListener('click', () => {
        storage.set('localStorage', CLICK_KEY, String(Date.now()));
        storage.set('sessionStorage', SHOWN_KEY, 'true');
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-visible')) this.dismiss();
      });

      if (this.dataset.designMode === 'true' || window.Shopify?.designMode) {
        window.setTimeout(() => this.show(), 300);
        return;
      }
      this.start();
    }

    async start() {
      if (storage.get('sessionStorage', SHOWN_KEY) === 'true') return;
      const now = Date.now();
      const clickedAt = number(storage.get('localStorage', CLICK_KEY), 0);
      if (clickedAt > 0 && now - clickedAt < 180 * 86400000) return;
      const dismissedAt = number(storage.get('localStorage', DISMISS_KEY), 0);
      const dismissDays = number(this.dataset.dismissDays, 30);
      if (dismissedAt > 0 && now - dismissedAt < dismissDays * 86400000) return;

      const views = number(storage.get('sessionStorage', PAGEVIEW_KEY), 0) + 1;
      storage.set('sessionStorage', PAGEVIEW_KEY, String(views));
      if (views < number(this.dataset.minPageviews, 2)) return;

      const eligibility = await this.eligibility();
      if (!eligibility?.eligible) return;
      if (eligibility.application_url && this.applyLink) this.applyLink.href = eligibility.application_url;

      await Promise.all([this.delay(), this.engagement()]);
      await this.waitForClearSurface();
      if (!anotherPromptIsOpen(this)) this.show();
    }

    async eligibility() {
      const cached = storage.get('localStorage', CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (number(parsed.expires_at, 0) > Date.now()) return parsed;
        } catch (_) { /* replace malformed cache below */ }
      }
      const endpoint = String(this.dataset.atlasEndpoint || '').trim();
      if (!endpoint) return null;
      try {
        const response = await fetch(endpoint, { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        const result = await response.json();
        const ttl = Math.min(86400, Math.max(900, number(result.ttl_seconds, result.eligible ? 86400 : 21600)));
        const cachedResult = {
          eligible: result.eligible === true,
          application_url: result.application_url || this.dataset.applicationUrl || '',
          expires_at: Date.now() + ttl * 1000
        };
        storage.set('localStorage', CACHE_KEY, JSON.stringify(cachedResult));
        return cachedResult;
      } catch (_) {
        return null;
      }
    }

    delay() {
      return new Promise((resolve) => window.setTimeout(resolve, number(this.dataset.delaySeconds, 12) * 1000));
    }

    engagement() {
      if (this.dataset.requireEngagement !== 'true' || window.scrollY > 140) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => {
          ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((name) => window.removeEventListener(name, done));
          resolve();
        };
        ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((name) => window.addEventListener(name, done, { once: true, passive: true }));
      });
    }

    async waitForClearSurface() {
      for (let attempt = 0; attempt < 12 && anotherPromptIsOpen(this); attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 5000));
      }
    }

    show() {
      if (this.classList.contains('is-visible')) return;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => this.classList.add('is-visible'));
      storage.set('sessionStorage', SHOWN_KEY, 'true');
      document.body.classList.add('db2-rep-popup-open');
      track('rungne_local_rep_popup_view');
    }

    dismiss() {
      storage.set('localStorage', DISMISS_KEY, String(Date.now()));
      storage.set('sessionStorage', SHOWN_KEY, 'true');
      document.body.classList.remove('db2-rep-popup-open');
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => { this.hidden = true; }, 400);
      track('rungne_local_rep_popup_dismiss');
    }
  }

  if (!customElements.get('db2-local-rep-popup')) customElements.define('db2-local-rep-popup', Db2LocalRepPopup);
})();
